import { ArrowRight, Mail, Newspaper } from "lucide-react";
import { Link } from "react-router-dom";
import { useMetaTags } from "../components/MetaTags";
import { formatReleaseDate, releases, syndication, syndicationTotals } from "../press/releases";

const BASE = "https://omnicanvasnotes.com";

/**
 * Fast facts exist so a journalist can attribute numbers without emailing to
 * check them. Every figure here has a single source of truth elsewhere in the
 * repo (PUBLIC_PLANS for price, the tools registry for the count) — if one
 * changes, this list has to change with it.
 */
const fastFacts: { label: string; value: string }[] = [
  {
    label: "What it is",
    value:
      "An infinite-canvas note-taking app that runs in the browser and installs as a PWA on Android, iOS, macOS, Windows, and Linux",
  },
  { label: "Free tier", value: "$0, with unlimited notes and canvases" },
  { label: "Pro tier", value: "$8 per month, or $80 per year" },
  { label: "Free tools", value: "40, none requiring an account or email address" },
  { label: "Encryption", value: "Client-side AES-256-GCM, available on the free tier" },
  { label: "Based in", value: "San Diego, California" },
];

const boilerplate =
  "OmniCanvas is an infinite-canvas note-taking application that runs in any modern " +
  "browser and installs as a progressive web app on Android, iOS, macOS, Windows, and Linux. " +
  "It combines handwriting, typed text, images, diagrams, and recorded audio on a single " +
  "spatial canvas, with client-side encryption available on every tier. OmniCanvas also " +
  "publishes a library of 40 free browser-based tools that require no account and process " +
  "files on the visitor's own device.";

export function Press() {
  useMetaTags({
    title: "Press & Media — OmniCanvas",
    description:
      "Press releases, boilerplate, fast facts and media contact for OmniCanvas, an infinite-canvas note-taking app with client-side encryption.",
    url: `${BASE}/press`,
  });

  return (
    <main className="pt-28 pb-24">
      <section className="mx-auto max-w-4xl px-6">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50 px-4 py-1.5 text-sm font-semibold text-indigo-700">
          <Newspaper size={15} />
          Press &amp; media
        </div>
        <h1 className="max-w-3xl text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
          Press room
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">
          Announcements, facts you can quote without checking with us first, and a
          direct line if you need something that is not here.
        </p>
      </section>

      <section className="mx-auto mt-14 max-w-4xl px-6">
        <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400">
          Fast facts
        </h2>
        <dl className="mt-4 grid gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 sm:grid-cols-2">
          {fastFacts.map((fact) => (
            <div key={fact.label} className="bg-white px-5 py-4">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                {fact.label}
              </dt>
              <dd className="mt-1 text-sm font-medium text-slate-800">{fact.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mx-auto mt-16 max-w-4xl px-6">
        <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400">
          Announcements
        </h2>
        {releases.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">
            No announcements yet. Check back soon.
          </p>
        ) : (
          <ol className="mt-4 space-y-4">
            {releases.map((release) => (
              <li
                key={release.slug}
                className="rounded-2xl border border-slate-200 bg-white p-6 transition-colors hover:border-indigo-200"
              >
                <time
                  dateTime={release.date}
                  className="text-xs font-semibold uppercase tracking-wide text-indigo-600"
                >
                  {formatReleaseDate(release.date)}
                </time>
                <h3 className="mt-2 text-xl font-bold leading-snug tracking-tight text-slate-900">
                  {release.headline}
                </h3>
                <p className="mt-2 text-sm font-medium leading-relaxed text-slate-500">
                  {release.subhead}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                  {release.summary}
                </p>
                <Link
                  to={release.href}
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                >
                  {release.hrefLabel}
                  <ArrowRight size={15} />
                </Link>

                {syndication[release.slug]?.length ? (
                  <div className="mt-5 border-t border-slate-100 pt-4">
                    <p className="text-xs text-slate-500">
                      Distributed by wire service and republished on{" "}
                      <strong className="font-semibold text-slate-700">
                        {syndicationTotals[release.slug] ?? syndication[release.slug].length}{" "}
                        distinct domains
                      </strong>
                      , mostly automated feeds rather than newsrooms. Placements we
                      checked by hand:
                    </p>
                    <ul className="mt-3 flex flex-wrap gap-2">
                      {syndication[release.slug].map((item) => (
                        <li key={item.url}>
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener nofollow"
                            className="inline-flex items-baseline gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-xs text-slate-600 transition-colors hover:border-indigo-200 hover:text-indigo-700"
                          >
                            <span className="font-semibold">{item.outlet}</span>
                            <span className="text-[11px] text-slate-400">{item.where}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="mx-auto mt-16 max-w-4xl px-6">
        <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400">
          About OmniCanvas
        </h2>
        <p className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm leading-relaxed text-slate-600">
          {boilerplate}
        </p>
        <p className="mt-4 text-sm leading-relaxed text-slate-600">
          OmniCanvas is built by the team behind{" "}
          <a
            href="https://inventivehq.com"
            className="font-semibold text-indigo-600 hover:underline"
          >
            InventiveHQ
          </a>
          , which publishes a free library of IT and security tools on the same principle:
          browser-based, no account required, nothing sent to a server that doesn&apos;t
          need it.
        </p>
      </section>

      <section className="mx-auto mt-16 max-w-4xl px-6">
        <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-6">
          <h2 className="flex items-center gap-2 text-lg font-bold tracking-tight text-slate-900">
            <Mail size={18} className="text-indigo-600" />
            Media contact
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            For interviews, product demos, screenshots, or fact-checking, get in touch
            and we will come back to you.
          </p>
          <Link
            to="/contact"
            className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700"
          >
            Contact us
            <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    </main>
  );
}
